import { useParams } from "react-router-dom";

import { env } from "../utils/env";

export interface MentionAttrs {
  id?: string;
  prefix?: string;
  title?: string;
  application?: { id?: string; slug?: string };
}

/**
 * Where a record mention opens: the app's record route, which finds the
 * record's solution itself. Null for a member or team mention, which a visitor
 * to a public form cannot open.
 */
export const recordMentionUrl = (
  accountId: string | undefined,
  attrs: MentionAttrs
): string | null => {
  const table = attrs.application?.id;
  const slug = attrs.application?.slug;
  if (
    !accountId ||
    attrs.prefix !== "#" ||
    !attrs.id ||
    !table ||
    slug === "members" ||
    slug === "teams"
  ) {
    return null;
  }
  return [env.REACT_APP_SMARTSUITE_APP_URL, accountId, "record", table, attrs.id]
    .map((part, index) => (index === 0 ? part.replace(/\/+$/, "") : encodeURIComponent(part)))
    .join("/");
};

/**
 * linkMentions shows the mentions in HTML the platform rendered the same way:
 * there a mention is an `<a class="mention">` holding its attrs as `mention-*`
 * attributes, which sanitizing strips, so this runs first.
 */
export const linkMentions = (html: string, accountId: string | undefined): string => {
  if (!html.includes("mention")) {
    return html;
  }
  const template = document.createElement("template");
  template.innerHTML = html;
  template.content.querySelectorAll("a.mention").forEach((anchor) => {
    const text = anchor.textContent ?? "";
    const href = recordMentionUrl(accountId, {
      id: anchor.getAttribute("mention-id") ?? "",
      prefix: text.charAt(0),
      application: {
        id: anchor.getAttribute("mention-application-id") ?? "",
        slug: anchor.getAttribute("mention-application-slug") ?? "",
      },
    });
    if (href) {
      anchor.setAttribute("href", href);
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
      return;
    }
    const span = document.createElement("span");
    span.className = "mention";
    span.textContent = text;
    anchor.replaceWith(span);
  });
  return template.innerHTML;
};

/** The workspace the form on this page belongs to, from its address. */
export const useFormAccountId = (): string | undefined => useParams().accountId;
